import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { UsersThree, Plus, Trash, PencilSimple, ShieldCheck, Sparkle } from "@phosphor-icons/react";
import api, { formatApiError, errContext } from "../../lib/api";

const ALL_MODULES = ["finance", "hr", "scm", "hse", "eam", "analytics", "copilot"];

// Ready-made role presets tenants can adopt with one tap.
// Modules are intersected with the tenant's actual subscription in the UI,
// so a preset that references an unavailable module simply won't include it.
const ROLE_PRESETS = [
  { key: "accountant", name: "Accountant", modules: ["finance"], description: "Books, journals, invoices — finance module only." },
  { key: "hr_manager", name: "HR Manager", modules: ["hr"], description: "Employee records, payroll, leave requests." },
  { key: "ops_lead", name: "Ops Lead", modules: ["scm", "eam", "hse"], description: "Supply chain, assets, health & safety oversight." },
  { key: "auditor", name: "Auditor", modules: ["analytics", "hse"], description: "Read-heavy: analytics + HSE incident reviews." },
];

/**
 * Custom Roles section — tenant admin creates named module bundles
 * (e.g. "Accountant" = [finance], "HR Manager" = [hr]).
 */
export default function CustomRolesSection({ tenantModules = ALL_MODULES }) {
  const [roles, setRoles] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/tenant/custom-roles");
      setRoles(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, errContext(err)));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleModule = (m) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(m)) n.delete(m); else n.add(m);
      return n;
    });
  };

  const reset = () => {
    setName(""); setDescription(""); setSelected(new Set()); setEditing(null);
  };

  const startEdit = (r) => {
    setEditing(r.id);
    setName(r.name);
    setDescription(r.description || "");
    setSelected(new Set(r.modules || []));
  };

  const save = async () => {
    if (!name.trim() || selected.size === 0) {
      toast.error("Name + at least one module are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = { name: name.trim(), modules: [...selected], description: description.trim() || null };
      if (editing) {
        await api.patch(`/tenant/custom-roles/${editing}`, payload);
        toast.success(`Role updated`);
      } else {
        await api.post("/tenant/custom-roles", payload);
        toast.success(`Role created`);
      }
      reset(); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, errContext(err)));
    } finally { setBusy(false); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete role "${r.name}"?`)) return;
    try {
      await api.delete(`/tenant/custom-roles/${r.id}`);
      toast.success("Role deleted"); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, errContext(err)));
    }
  };

  // One-tap preset adoption — POSTs directly with modules clamped to the tenant's subscription.
  const applyPreset = async (preset) => {
    if (roles.some((r) => r.name.toLowerCase() === preset.name.toLowerCase())) {
      toast.info(`"${preset.name}" already exists — edit it from the list.`);
      return;
    }
    const modules = preset.modules.filter((m) => tenantModules.includes(m));
    if (modules.length === 0) {
      toast.error(`None of ${preset.name}'s modules (${preset.modules.join(", ")}) are in your subscription.`);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/tenant/custom-roles?preset_key=${encodeURIComponent(preset.key)}`, {
        name: preset.name, modules, description: preset.description,
      });
      const skipped = preset.modules.filter((m) => !modules.includes(m));
      toast.success(`Added "${preset.name}" with ${modules.length} module(s)${skipped.length ? ` (skipped: ${skipped.join(", ")})` : ""}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, errContext(err)));
    } finally { setBusy(false); }
  };

  return (
    <section className="mt-8 border border-zinc-950" data-testid="custom-roles-section">
      <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase flex items-center gap-2">
        <UsersThree size={12} /> CUSTOM_ROLES
        <span className="text-[9px] text-zinc-400 ml-2">({roles.length} defined)</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr]">
        {/* Existing roles */}
        <div className="border-r border-zinc-200">
          {roles.length === 0 ? (
            <div className="p-8 text-center font-mono text-xs text-zinc-400">NO CUSTOM ROLES YET · CREATE ONE →</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                <tr>
                  <th className="text-left px-4 py-2.5">Role</th>
                  <th className="text-left px-4 py-2.5">Modules</th>
                  <th className="text-right px-4 py-2.5">Users</th>
                  <th className="w-24"></th>
                </tr>
              </thead>
              <tbody data-testid="custom-roles-tbody">
                {roles.map((r) => (
                  <tr key={r.id} className="border-b border-zinc-100 align-top hover:bg-zinc-50">
                    <td className="px-4 py-3">
                      <div className="font-semibold">{r.name}</div>
                      {r.description && <div className="text-[11px] text-zinc-500 mt-0.5">{r.description}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {r.modules.map((m) => (
                          <span key={m} className="font-mono text-[10px] bg-zinc-100 border border-zinc-200 px-1.5 py-0.5">
                            {m}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right tnum text-sm">{r.user_count ?? 0}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => startEdit(r)} className="p-1.5 hover:bg-zinc-200" title="Edit" data-testid={`edit-role-${r.id}`}>
                          <PencilSimple size={13} />
                        </button>
                        <button onClick={() => del(r)} className="p-1.5 hover:bg-red-100 text-[#D0021B]" title="Delete" data-testid={`delete-role-${r.id}`}>
                          <Trash size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Create / edit form */}
        <div className="p-5 bg-zinc-50/50">
          {!editing && (
            <div className="mb-4 pb-4 border-b border-zinc-200">
              <div className="font-mono text-[10px] tracking-[0.18em] text-zinc-500 uppercase inline-flex items-center gap-1.5">
                <Sparkle size={11} weight="fill" className="text-[#FF3E00]" /> QUICK PRESETS
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">One-tap templates. Modules not in your subscription are skipped automatically.</div>
              <div className="mt-2 grid grid-cols-2 gap-1.5" data-testid="role-presets-grid">
                {ROLE_PRESETS.map((p) => {
                  const exists = roles.some((r) => r.name.toLowerCase() === p.name.toLowerCase());
                  const available = p.modules.filter((m) => tenantModules.includes(m));
                  const disabled = exists || available.length === 0 || busy;
                  return (
                    <button
                      key={p.key}
                      onClick={() => applyPreset(p)}
                      disabled={disabled}
                      data-testid={`role-preset-${p.key}`}
                      title={exists ? "Already exists" : (available.length === 0 ? "No matching modules in your plan" : p.description)}
                      className={`text-left border p-2 transition-colors ${exists ? "bg-zinc-100 border-zinc-200 text-zinc-400" : "bg-white border-zinc-300 hover:border-zinc-950 hover:bg-white"}`}
                    >
                      <div className="font-mono text-[11px] font-bold uppercase tracking-[0.05em] flex items-center justify-between">
                        <span>{p.name}</span>
                        {exists && <span className="text-[9px] text-emerald-600 font-normal">✓ ADDED</span>}
                      </div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">
                        {available.length > 0 ? available.join(" · ") : <span className="text-[#D0021B]">— not in plan —</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="font-mono text-[10px] tracking-[0.18em] text-zinc-500 uppercase">
            {editing ? "/ EDIT ROLE" : "/ NEW ROLE (CUSTOM)"}
          </div>
          <input
            data-testid="role-name-input"
            type="text" placeholder="e.g. Accountant"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-2 w-full font-display font-black text-2xl border-b-2 border-zinc-950 bg-transparent outline-none focus:border-[#FF3E00]"
          />
          <textarea
            data-testid="role-desc-input"
            placeholder="Short description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="mt-3 w-full text-sm border border-zinc-200 p-2 h-16 outline-none focus:border-zinc-950 bg-white"
          />
          <div className="mt-3 font-mono text-[10px] tracking-[0.15em] text-zinc-500 uppercase">Modules for this role</div>
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            {tenantModules.map((m) => {
              const on = selected.has(m);
              return (
                <button
                  key={m}
                  onClick={() => toggleModule(m)}
                  data-testid={`role-mod-${m}`}
                  className={`font-mono text-[11px] px-3 py-2 border text-left transition-colors ${on ? "bg-zinc-950 text-white border-zinc-950" : "bg-white border-zinc-300 hover:border-zinc-950"}`}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <ShieldCheck size={11} weight={on ? "fill" : "regular"} className={on ? "text-[#FF3E00]" : "text-zinc-400"} />
                    {m}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={save}
              disabled={busy}
              data-testid="save-role-btn"
              className="flex-1 bg-zinc-950 text-white font-mono text-[11px] tracking-[0.15em] uppercase py-2.5 hover:bg-[#FF3E00] disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2">
              <Plus size={12} /> {editing ? "UPDATE" : "CREATE ROLE"}
            </button>
            {editing && (
              <button onClick={reset} className="font-mono text-[10px] tracking-[0.12em] uppercase border border-zinc-300 px-4 hover:bg-zinc-100">
                CANCEL
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
